import { FC, MouseEvent, ReactNode } from 'react';
import { Stack, Theme, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { createUseStyles } from 'react-jss';
import { FormProps } from '../helpers';
import FormControl from '../form-control';
import Text from '@/components/text';
import { OnOffToggleButtonValue } from './helpers';

const useStyles = createUseStyles<string, { isSmall: boolean }>((theme: Theme) => ({
  onOffToggleButton: ({ isSmall }) => ({
    '& .MuiButtonBase-root': {
      padding: isSmall ? 2 : 4,
      textTransform: 'none',
      width: isSmall ? 40 : 50,
      fontSize: theme.typography.body2.fontSize,
      fontWeight: 'normal',
      '&.not-set': {
        width: isSmall ? 65 : 80,
      },
    },
    '& .MuiButtonBase-root.Mui-selected.Mui-disabled': {
      color: theme.palette.lightGray,
    },
    '& .MuiToggleButtonGroup-root': {
      backgroundColor: theme.palette.common.white,
    },
  }),
}));

const TOGGLE_OPTIONS: { value: OnOffToggleButtonValue; label: ReactNode; className: string }[] = [
  { value: undefined, label: 'Inherit', className: 'not-set' },
  { value: false, label: 'Off', className: 'off' },
  { value: true, label: 'On', className: 'on' },
];

const valueToString = (value: OnOffToggleButtonValue): string => {
  if (value === true) {
    return 'true';
  }
  if (value === false) {
    return 'false';
  }
  return 'undefined';
};

const stringToValue = (str: string): OnOffToggleButtonValue => {
  if (str === 'true') {
    return true;
  }
  if (str === 'false') {
    return false;
  }
  return undefined;
};

interface Props extends FormProps {
  value?: OnOffToggleButtonValue;
  onChange: (value: OnOffToggleButtonValue) => void;
  inline?: boolean;
  withNotSet?: boolean;
}

const OnOffToggleButton: FC<Props> = ({
  label,
  value,
  onChange,
  error,
  name,
  fullWidth,
  disabled,
  readonly,
  inline = true,
  size = 'medium',
  withNotSet = true,
}) => {
  const isSmall = size === 'small' || size === 'x-small';
  const styles = useStyles({ isSmall });
  const selectedValue = valueToString(value);

  const handleChange = (_: MouseEvent<HTMLElement>, newValue: string | null) => {
    onChange(newValue !== null ? stringToValue(newValue) : undefined);
  };

  const hasLabel = !!label;
  const flexDirection = inline ? 'row' : 'column';
  const flexAlign = inline ? 'center' : 'flex-start';
  const toggleOptions = withNotSet
    ? TOGGLE_OPTIONS
    : TOGGLE_OPTIONS.filter((option) => option.value !== undefined);

  return (
    <FormControl label="" error={error} name={name} fullWidth={fullWidth}>
      <Stack direction={flexDirection} gap={2} alignItems={flexAlign}>
        {hasLabel && <Text size={size}>{label}</Text>}
        <Stack direction="row" alignItems="center" className={styles.onOffToggleButton}>
          <ToggleButtonGroup
            value={selectedValue}
            exclusive
            onChange={handleChange}
            disabled={disabled || readonly}
            size={size === 'x-small' || size === 'small' ? 'small' : 'medium'}
          >
            {toggleOptions.map(({ value, label, className }) => (
              <ToggleButton key={String(value)} value={valueToString(value)} className={className}>
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </FormControl>
  );
};

export default OnOffToggleButton;
